import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  IconButton,
  Modal,
  Grid,
  TextField,
  Popover,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  CircularProgress
} from '@mui/material';
import Swal from 'sweetalert2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BlockIcon from '@mui/icons-material/Block';
import { getClientes } from '../../auxiliares/maestros';
import { workWithLocalStorage, buildBody } from '../../auxiliares/funciones';
import CONFIG from '../../CONFIG.json';

// Paleta de colores suaves para filas y celdas
const COLOR_PALETTE = [
  { name: 'Sin Color / Blanco', value: '#FFFFFF' },
  { name: 'Amarillo', value: '#FFFF00' },
  { name: 'Verde Pistacho', value: '#92D050' },
  { name: 'Azul', value: '#00B0F0' },
  { name: 'Rosa', value: '#FF66FF' },
  { name: 'Marrón', value: '#ED7D31' },
  { name: 'Verde', value: '#00B050' },
  { name: 'Azul Claro', value: '#BDD7EE' },
];

const COLUMNS = [
  { key: 'time', label: 'Time', width: 80 },
  { key: 'cf', label: 'CF', width: 55 },
  { key: 'room', label: 'ROOM', width: 60 },
  { key: 'ther', label: 'THER.', width: 75 },
  { key: 'recep', label: 'RECEP.', width: 75 },
  { key: 'name', label: 'NAME', width: 175 },
  { key: 'telephone', label: 'Telephone', width: 110 },
  { key: 'treatment', label: 'Treatment', width: 185 },
  { key: 'duration', label: 'Duration', width: 70 },
  { key: 'comment', label: 'COMMENT', width: 135 },
  { key: 'scrub', label: 'SCRUB', width: 70 },
  { key: 'oil', label: 'OIL', width: 75 },
  { key: 'ticket_no', label: 'TICKET Nº', width: 85 },
  { key: 'cash', label: 'CASH', numeric: true, width: 70 },
  { key: 'visa', label: 'VISA', numeric: true, width: 70 },
  { key: 'transfer', label: 'TRANSFER', numeric: true, width: 75 },
  { key: 'treatmens', label: 'TREATMENS', width: 130 },
];

const ADICIONALES_COLUMNS = [
  { key: 'personal', label: 'PERSONAL', width: 160 },
  { key: 'concepto', label: 'CONCEPTO / TRATAMIENTO', width: 320 },
  { key: 'pax_cliente', label: 'PAX / CLIENTE', width: 220 },
  { key: 'codigo_ref', label: 'CÓDIGO / REF', width: 220 },
  { key: 'forma_pago', label: 'FORMA DE PAGO', width: 170 },
  { key: 'precio', label: 'PRECIO', numeric: true, width: 100 },
];

const MESES = [
  'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
  'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'
];

const getDaysInMonth = (year, month) => {
  const days = [];
  const numDays = new Date(year, month, 0).getDate();
  for (let d = 1; d <= numDays; d += 1) {
    const dayStr = String(d).padStart(2, '0');
    const monthStr = String(month).padStart(2, '0');
    const dateStr = `${year}-${monthStr}-${dayStr}`;

    const dateObj = new Date(year, month - 1, d);
    const dayName = dateObj.toLocaleDateString('es-ES', { weekday: 'long' }).toUpperCase();
    days.push({ dateStr, dayName, dayNum: d });
  }
  return days;
};

const getCalendarMatrix = (year, month) => {
  const firstDayIndex = new Date(year, month - 1, 1).getDay();
  const offset = (firstDayIndex + 6) % 7;
  const numDays = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < offset; i += 1) {
    cells.push(null);
  }
  for (let d = 1; d <= numDays; d += 1) {
    cells.push(d);
  }
  return cells;
};

// Helper para normalizar textos sin tildes ni caracteres especiales
const normalizeText = (str) =>
  (str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

// Helper para ordenar citas por Time y Room en orden ascendente (sin hora al final)
const sortCitasByTimeAndRoom = (rows) =>
  [...(rows || [])].sort((a, b) => {
    const timeA = String(a.datos?.time || '').trim().toLowerCase();
    const timeB = String(b.datos?.time || '').trim().toLowerCase();

    if (timeA !== timeB) {
      if (!timeA) return 1;
      if (!timeB) return -1;
      return timeA.localeCompare(timeB, undefined, { numeric: true });
    }

    const roomA = String(a.datos?.room || '').trim().toLowerCase();
    const roomB = String(b.datos?.room || '').trim().toLowerCase();

    if (roomA !== roomB) {
      if (!roomA) return 1;
      if (!roomB) return -1;
      return roomA.localeCompare(roomB, undefined, { numeric: true });
    }

    const nameA = String(a.datos?.name || '').trim().toLowerCase();
    const nameB = String(b.datos?.name || '').trim().toLowerCase();

    if (nameA !== nameB) {
      if (!nameA) return 1;
      if (!nameB) return -1;
      return nameA.localeCompare(nameB);
    }

    return (a.id || 0) - (b.id || 0);
  });

// Helper para parsear la duración en minutos
const parseDurationToMinutes = (val) => {
  if (val === undefined || val === null) return 0;
  const str = String(val).trim();
  if (!str) return 0;

  // Formato HH:MM o H:MM (ej: "1:00", "2:15", "0:45", "01:30")
  if (str.includes(':')) {
    const parts = str.split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return h * 60 + m;
  }

  // Formato decimal con coma o punto (ej: "1.5", "1,5", "2.25")
  const normalized = str.replace(',', '.');
  const num = parseFloat(normalized);
  if (Number.isNaN(num) || num <= 0) return 0;

  // Si tiene decimales o es un entero pequeño (<15), asumimos horas (ej: 1 = 1h = 60m, 1.5 = 90m, 2 = 120m)
  if (normalized.includes('.') || num < 15) {
    return Math.round(num * 60);
  }

  // Si es un entero >= 15 (ej: 20, 30, 45, 60, 90), asumimos minutos
  return Math.round(num);
};

// Helper para calcular el sumatorio de duración formateado a HH:mm
const calculateTotalDuration = (rows) => {
  const totalMinutes = (rows || []).reduce((sum, row) => sum + parseDurationToMinutes(row.datos?.duration), 0);
  if (totalMinutes === 0) return '';
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

// Helper para navegación con flechas estilo Excel entre celdas
const handleCellNavKeyDown = (e, dateStr, rowIdx, colIdx) => {
  const input = e.target;
  const isInput = input && input.tagName === 'INPUT';
  const isSelect = input && input.tagName === 'SELECT';

  if (e.key === 'ArrowDown' || e.key === 'Enter') {
    e.preventDefault();
    const targetEl = document.querySelector(`[data-nav-day="${dateStr}"][data-nav-row="${rowIdx + 1}"][data-nav-col="${colIdx}"]`);
    if (targetEl) {
      targetEl.focus();
      if (targetEl.select) targetEl.select();
    }
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    const targetEl = document.querySelector(`[data-nav-day="${dateStr}"][data-nav-row="${rowIdx - 1}"][data-nav-col="${colIdx}"]`);
    if (targetEl) {
      targetEl.focus();
      if (targetEl.select) targetEl.select();
    }
  } else if (e.key === 'ArrowRight') {
    if (isSelect || !isInput || (input.selectionStart === input.selectionEnd && input.selectionEnd === (input.value || '').length)) {
      e.preventDefault();
      const targetEl = document.querySelector(`[data-nav-day="${dateStr}"][data-nav-row="${rowIdx}"][data-nav-col="${colIdx + 1}"]`);
      if (targetEl) {
        targetEl.focus();
        if (targetEl.select) targetEl.select();
      }
    }
  } else if (e.key === 'ArrowLeft') {
    if (isSelect || !isInput || (input.selectionStart === input.selectionEnd && input.selectionStart === 0)) {
      e.preventDefault();
      const targetEl = document.querySelector(`[data-nav-day="${dateStr}"][data-nav-row="${rowIdx}"][data-nav-col="${colIdx - 1}"]`);
      if (targetEl) {
        targetEl.focus();
        if (targetEl.select) targetEl.select();
      }
    }
  }
};

// Helper para comprobar si una fila está totalmente vacía
const isRowEmpty = (row) => {
  if (!row) return true;
  if (row.cliente_id || row.servicio_id || row.room_id || (row.color_fila && row.color_fila !== '#FFFFFF')) return false;
  const d = row.datos || {};
  const keys = Object.keys(d);
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (k !== 'cell_colors' && k !== 'cancelado') {
      const val = d[k];
      if (val !== undefined && val !== null && String(val).trim() !== '' && val !== 0 && val !== '0') {
        return false;
      }
    }
  }
  return true;
};

// Helper para comprobar si una fila adicional está totalmente vacía
const isAdicionalRowEmpty = (row) => {
  if (!row) return true;
  if (row.color_fila && row.color_fila !== '#FFFFFF') return false;
  const d = row.datos || {};
  const keys = Object.keys(d);
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (k !== 'cell_colors') {
      const val = d[k];
      if (val !== undefined && val !== null && String(val).trim() !== '' && val !== 0 && val !== '0') {
        return false;
      }
    }
  }
  return true;
};

const ensureTrailingBlankAdicional = (dayRows, fecha) => {
  const rows = [...(dayRows || [])];
  const lastRow = rows[rows.length - 1];
  if (!lastRow || !isAdicionalRowEmpty(lastRow)) {
    rows.push({
      id: null,
      fecha,
      orden: rows.length,
      color_fila: '',
      datos: { personal: '', concepto: '', pax_cliente: '', codigo_ref: '', forma_pago: '', precio: 0, cell_colors: {} }
    });
  }
  return rows;
};

// COMPONENTE DE CELDA ULTRA RÁPIDO CON PROTECCIÓN DE CAMBIO DE FOCO Y NAVEGACIÓN EXCEL
const CellInput = React.memo(({ initialValue, numeric, textColor, onCellBlur, dataNavDay, dataNavRow, dataNavCol, onKeyDown }) => {
  const [val, setVal] = useState(initialValue ?? '');
  const initialRef = useRef(initialValue ?? '');

  useEffect(() => {
    setVal(initialValue ?? '');
    initialRef.current = initialValue ?? '';
  }, [initialValue]);

  return (
    <input
      type={numeric ? 'number' : 'text'}
      value={val}
      data-nav-day={dataNavDay}
      data-nav-row={dataNavRow}
      data-nav-col={dataNavCol}
      onFocus={(e) => {
        if (e.target) e.target.select();
      }}
      onChange={(e) => {
        setVal(e.target.value);
      }}
      onKeyDown={onKeyDown}
      onBlur={() => {
        const currentTrim = String(val ?? '').trim();
        const initialTrim = String(initialRef.current ?? '').trim();
        if (currentTrim !== initialTrim) {
          initialRef.current = val;
          if (onCellBlur) onCellBlur(val);
        }
      }}
      style={{
        width: '100%',
        border: 'none',
        background: 'transparent',
        outline: 'none',
        textAlign: numeric ? 'right' : 'left',
        fontSize: '12px',
        padding: '2px 4px',
        fontWeight: textColor ? 700 : 400,
        color: textColor || 'inherit'
      }}
    />
  );
});

// COMPONENTE DE NOMBRE ULTRA LIGERO CON AUTOCOMPLETADO FLOTANTE Y NAVEGACIÓN EXCEL
const NameCell = React.memo(
  ({ cellValue, clientesList, dateStr, row, idx, textColor, onSelectClient, onNameBlur, dataNavCol, onNavKeyDown }) => {
    const [inputVal, setInputVal] = useState(cellValue || '');
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const initialValRef = useRef(cellValue || '');
    const isSelectingRef = useRef(false);

    useEffect(() => {
      setInputVal(cellValue || '');
      initialValRef.current = cellValue || '';
    }, [cellValue]);

    const matchingClients = useMemo(() => {
      if (!isOpen) return [];
      const queryClean = normalizeText(inputVal);
      if (queryClean.length < 3) return [];

      const queryWords = queryClean.split(/\s+/).filter(Boolean);
      const results = [];
      const list = clientesList || [];
      for (let i = 0; i < list.length; i += 1) {
        const o = list[i];
        const targetText = normalizeText(
          `${o.nombre || ''} ${o.apellidos || ''} ${o.telefono || ''}`
        );
        if (queryWords.every((w) => targetText.includes(w))) {
          results.push(o);
          if (results.length >= 25) break;
        }
      }
      return results;
    }, [isOpen, inputVal, clientesList]);

    const handleSelect = (client) => {
      isSelectingRef.current = true;
      const fullName = `${client.nombre || ''} ${client.apellidos || ''}`.trim();
      setInputVal(fullName);
      initialValRef.current = fullName;
      setIsOpen(false);
      onSelectClient(dateStr, row.id, idx, client);
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    };

    const handleBlur = () => {
      setTimeout(() => {
        if (isSelectingRef.current) return;
        setIsOpen(false);
        const trimmed = (inputVal || '').trim();
        const prevTrimmed = (initialValRef.current || '').trim();
        if (trimmed !== prevTrimmed) {
          initialValRef.current = inputVal;
          onNameBlur(dateStr, row.id, idx, inputVal);
        }
      }, 150);
    };

    const handleKeyDown = (e) => {
      if (isOpen && matchingClients.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex((prev) => (prev + 1) % matchingClients.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex((prev) => (prev - 1 + matchingClients.length) % matchingClients.length);
          return;
        }
        if (e.key === 'Enter') {
          if (matchingClients[highlightedIndex]) {
            e.preventDefault();
            handleSelect(matchingClients[highlightedIndex]);
            return;
          }
        }
        if (e.key === 'Escape') {
          setIsOpen(false);
          return;
        }
      }
      if (onNavKeyDown) {
        onNavKeyDown(e, dateStr, idx, dataNavCol);
      }
    };

    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          type="text"
          value={inputVal}
          data-nav-day={dateStr}
          data-nav-row={idx}
          data-nav-col={dataNavCol}
          onFocus={(e) => {
            if (e.target) e.target.select();
            if (normalizeText(inputVal).length >= 3) {
              setIsOpen(true);
            }
          }}
          onChange={(e) => {
            const nextVal = e.target.value;
            setInputVal(nextVal);
            setHighlightedIndex(0);
            if (normalizeText(nextVal).length >= 3) {
              setIsOpen(true);
            } else {
              setIsOpen(false);
            }
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            outline: 'none',
            textAlign: 'left',
            fontSize: '12px',
            padding: '2px 4px',
            fontWeight: textColor ? 700 : 500,
            color: textColor || 'inherit'
          }}
        />
        {isOpen && matchingClients.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              minWidth: '280px',
              maxWidth: '380px',
              maxHeight: '220px',
              overflowY: 'auto',
              backgroundColor: '#FFFFFF',
              border: '1px solid #CBD5E1',
              borderRadius: '6px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
              zIndex: 9999,
              padding: '4px 0',
              marginTop: '2px'
            }}
          >
            {matchingClients.map((client, cIdx) => {
              const isDeactivated = client.activo === 0 || client.estado === 'Borrado';
              const isHighlight = cIdx === highlightedIndex;
              let itemFontWeight = 400;
              if (isDeactivated) {
                itemFontWeight = 'bold';
              } else if (isHighlight) {
                itemFontWeight = 600;
              }

              return (
                <div
                  key={client.id}
                  role="option"
                  aria-selected={isHighlight}
                  tabIndex={-1}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(client);
                  }}
                  onMouseEnter={() => setHighlightedIndex(cIdx)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    backgroundColor: isHighlight ? '#F1F5F9' : '#FFFFFF',
                    color: isDeactivated ? '#DC2626' : '#1E293B',
                    fontWeight: itemFontWeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #F8FAFC'
                  }}
                >
                  <span>
                    {client.label || `${client.nombre} ${client.apellidos}`}
                    {isDeactivated && ' ⚠️ (DESACTIVADO)'}
                  </span>
                  {client.telefono && (
                    <span style={{ color: '#64748B', fontSize: '11px', marginLeft: '8px' }}>
                      {client.telefono}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

// COMPONENTE DE ENTRADA PARA TURNO DE PERSONAL
const PersonalInput = React.memo(({ pItem, onPersonalSave }) => {
  const [val, setVal] = useState(pItem.estado_horario || '');
  const initialRef = useRef(pItem.estado_horario || '');

  useEffect(() => {
    setVal(pItem.estado_horario || '');
    initialRef.current = pItem.estado_horario || '';
  }, [pItem.estado_horario]);

  return (
    <input
      type="text"
      placeholder="Turno/Estado"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        const currentTrim = String(val ?? '').trim();
        const initialTrim = String(initialRef.current ?? '').trim();
        if (currentTrim !== initialTrim) {
          initialRef.current = val;
          onPersonalSave(val);
        }
      }}
      style={{
        padding: '4px 8px',
        borderRadius: '6px',
        border: '1px solid #CBD5E1',
        backgroundColor: '#FFFFFF',
        fontSize: '12px',
        outline: 'none',
        width: '130px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
      }}
    />
  );
});

// COMPONENTE DE ENTRADA PARA NOTAS DEL DÍA (COLUMNA CENTRAL)
const DiaNotaInput = React.memo(({ dateStr, nota, onNotaSave }) => {
  const [val, setVal] = useState(nota || '');
  const initialRef = useRef(nota || '');

  useEffect(() => {
    setVal(nota || '');
    initialRef.current = nota || '';
  }, [nota, dateStr]);

  const handleBlur = () => {
    if (val !== initialRef.current) {
      initialRef.current = val;
      onNotaSave(dateStr, val);
    }
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '110px' }}>
      <textarea
        placeholder="Escribe aquí las notas y observaciones del día..."
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={handleBlur}
        style={{
          flex: 1,
          width: '100%',
          minHeight: '100px',
          boxSizing: 'border-box',
          padding: '8px 10px',
          borderRadius: '6px',
          border: '1px solid #CBD5E1',
          backgroundColor: '#FFFFFF',
          fontSize: '13px',
          fontFamily: 'inherit',
          lineHeight: '1.4',
          resize: 'vertical',
          outline: 'none',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
        }}
      />
    </Box>
  );
});

// COMPONENTE MEMOIZADO DE LA TARJETA DEL DÍA
const DiaCard = React.memo(
  ({
    dateStr,
    dayName,
    dayNum,
    dayRows,
    dayPersonal,
    dayAdicionales,
    dayNota,
    onNotaSave,
    isPersonalCollapsed,
    draggedRow,
    dragOverTarget,
    clientesList,
    serviciosUnicos,
    rooms,
    onInsertRow,
    onDeleteRow,
    onToggleCancelRow,
    onDropRow,
    onOpenColorPicker,
    onCellBlurSave,
    onSelectClient,
    onNameBlur,
    onAddAdicionalRow,
    onDeleteAdicionalRow,
    onAdicionalCellBlurSave,
    onPersonalSave,
    onOpenAddPersonal,
    togglePersonalCollapse,
    onRefreshDay,
    setDraggedRow,
    setDragOverTarget
  }) => {
    const terapeutas = useMemo(() => dayPersonal.filter((p) => p.tipo_personal === 'TERAPEUTA'), [dayPersonal]);
    const recepcionistas = useMemo(() => dayPersonal.filter((p) => p.tipo_personal === 'RECEPCIONISTA'), [dayPersonal]);

    const calculateTotal = (rows, colKey) =>
      (rows || []).reduce((sum, row) => sum + (parseFloat(row.datos?.[colKey]) || 0), 0);

    const totalCitas =
      calculateTotal(dayRows, 'cash') +
      calculateTotal(dayRows, 'visa') +
      calculateTotal(dayRows, 'transfer');

    const totalAdicionales = calculateTotal(dayAdicionales, 'precio');
    const totalDia = totalCitas + totalAdicionales;

    const renderCellInput = (col, row, idx, cellValue, colIdx) => {
      const isCancelled = !!row.datos?.cancelado;
      const rowTextColor = isCancelled ? '#DC2626' : undefined;

      if (col.key === 'name') {
        return (
          <NameCell
            cellValue={cellValue}
            clientesList={clientesList}
            dateStr={dateStr}
            row={row}
            idx={idx}
            textColor={rowTextColor}
            onSelectClient={onSelectClient}
            onNameBlur={onNameBlur}
            dataNavCol={colIdx}
            onNavKeyDown={handleCellNavKeyDown}
          />
        );
      }

      if (col.key === 'room') {
        return (
          <select
            value={cellValue || ''}
            data-nav-day={dateStr}
            data-nav-row={idx}
            data-nav-col={colIdx}
            onKeyDown={(e) => handleCellNavKeyDown(e, dateStr, idx, colIdx)}
            onChange={(e) => onCellBlurSave(dateStr, row.id, idx, 'room', e.target.value)}
            style={{
              width: '100%',
              padding: '2px 4px',
              borderRadius: '4px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FFFFFF',
              fontSize: '12px',
              fontWeight: isCancelled ? 700 : 500,
              color: rowTextColor || '#1E293B',
              cursor: 'pointer',
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              transition: 'all 0.15s ease'
            }}
          >
            <option value="">-- Sala --</option>
            {(rooms || []).map((r) => (
              <option key={r.id} value={r.codigo || r.nombre}>
                {r.codigo || r.nombre}
              </option>
            ))}
          </select>
        );
      }

      if (col.key === 'treatment') {
        return (
          <select
            value={cellValue || ''}
            data-nav-day={dateStr}
            data-nav-row={idx}
            data-nav-col={colIdx}
            onKeyDown={(e) => handleCellNavKeyDown(e, dateStr, idx, colIdx)}
            onChange={(e) => onCellBlurSave(dateStr, row.id, idx, 'treatment', e.target.value)}
            style={{
              width: '100%',
              padding: '2px 4px',
              borderRadius: '4px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FFFFFF',
              fontSize: '12px',
              fontWeight: isCancelled ? 700 : 500,
              color: rowTextColor || '#1E293B',
              cursor: 'pointer',
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              transition: 'all 0.15s ease'
            }}
          >
            <option value="">-- Servicio --</option>
            {serviciosUnicos.map((s) => (
              <option key={s.id || s.nombre} value={s.nombre}>
                {s.nombre}
              </option>
            ))}
          </select>
        );
      }

      return (
        <CellInput
          initialValue={cellValue}
          numeric={col.numeric}
          textColor={rowTextColor}
          dataNavDay={dateStr}
          dataNavRow={idx}
          dataNavCol={colIdx}
          onKeyDown={(e) => handleCellNavKeyDown(e, dateStr, idx, colIdx)}
          onCellBlur={(newVal) => onCellBlurSave(dateStr, row.id, idx, col.key, newVal)}
        />
      );
    };

    return (
      <Card id={`dia-card-${dateStr}`} sx={{ mb: 4, borderRadius: 2, boxShadow: 3, overflow: 'visible' }}>
        {/* CABECERA DEL DÍA */}
        <Box
          sx={{
            background: '#0F172A',
            color: '#F8FAFC',
            py: 1.5,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {dayName} {dayNum} ({dateStr})
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => onInsertRow(dateStr, dayRows.length)}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '13px',
                bgcolor: '#10B981',
                color: '#FFFFFF',
                '&:hover': { bgcolor: '#059669' }
              }}
            >
              + Añadir Cita
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={onRefreshDay}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '13px',
                bgcolor: '#2563EB',
                color: '#FFFFFF',
                '&:hover': { bgcolor: '#1D4ED8' }
              }}
            >
              Actualizar Citas del Día
            </Button>
          </Stack>
        </Box>

        <CardContent sx={{ p: 2 }}>
          {/* SUBTABLA DE ESTADO DEL PERSONAL EN 2 COLUMNAS (THER A LA IZQUIERDA Y RECEP A LA DERECHA) */}
          <Box sx={{ mb: 2.5, p: 2, bg: '#F8FAFC', borderRadius: 1.5, border: '1px solid #CBD5E1' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: isPersonalCollapsed ? 0 : 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton
                  size="small"
                  onClick={() => togglePersonalCollapse(dateStr)}
                  sx={{ color: '#0F172A' }}
                >
                  {isPersonalCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
                <Typography
                  variant="subtitle1"
                  onClick={() => togglePersonalCollapse(dateStr)}
                  sx={{ fontWeight: 'bold', color: '#0F172A', cursor: 'pointer', userSelect: 'none' }}
                >
                  👥 Estado del Personal / Recepción
                </Typography>
              </Stack>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => onOpenAddPersonal(dateStr)}
              >
                + Añadir Personal
              </Button>
            </Stack>

            <Collapse in={!isPersonalCollapsed} timeout="auto">
              <Grid container spacing={3} sx={{ pt: 1 }} alignItems="stretch">
                {/* COLUMNA IZQUIERDA: TERAPEUTAS (THER) */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 1.5, color: '#1E293B', borderBottom: '2px solid #3B82F6', pb: 0.5 }}
                  >
                    💆 TERAPEUTAS (THER)
                  </Typography>
                  {terapeutas.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      Sin terapeutas registradas.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {terapeutas.map((p) => (
                        <Stack direction="row" spacing={1} alignItems="center" key={p.id || p.nombre}>
                          <Typography variant="body2" sx={{ minWidth: 90, fontWeight: 700, fontSize: '13px', color: '#0F172A' }}>
                            {p.nombre}:
                          </Typography>
                          <PersonalInput
                            pItem={p}
                            onPersonalSave={(newVal) => onPersonalSave(dateStr, p, newVal)}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Grid>

                {/* COLUMNA CENTRAL: NOTAS / OBSERVACIONES */}
                <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 1.5, color: '#1E293B', borderBottom: '2px solid #8B5CF6', pb: 0.5 }}
                  >
                    📝 NOTAS / OBSERVACIONES
                  </Typography>
                  <DiaNotaInput
                    dateStr={dateStr}
                    nota={dayNota}
                    onNotaSave={onNotaSave}
                  />
                </Grid>

                {/* COLUMNA DERECHA: RECEPCIONISTAS (RECEP) */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 1.5, color: '#1E293B', borderBottom: '2px solid #10B981', pb: 0.5 }}
                  >
                    📞 RECEPCIÓN (RECEP)
                  </Typography>
                  {recepcionistas.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      Sin recepcionistas registradas.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {recepcionistas.map((p) => (
                        <Stack direction="row" spacing={1} alignItems="center" key={p.id || p.nombre}>
                          <Typography variant="body2" sx={{ minWidth: 90, fontWeight: 700, fontSize: '13px', color: '#0F172A' }}>
                            {p.nombre}:
                          </Typography>
                          <PersonalInput
                            pItem={p}
                            onPersonalSave={(newVal) => onPersonalSave(dateStr, p, newVal)}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Grid>
              </Grid>
            </Collapse>
          </Box>

          {/* TABLA PRINCIPAL ESTILO EXCEL CON SCROLL HORIZONTAL */}
          <Box sx={{ overflowX: 'auto', border: '1px solid #CBD5E1', borderRadius: '4px' }}>
            <table
              style={{
                minWidth: '1515px',
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '12px',
                fontFamily: 'sans-serif'
              }}
            >
              <thead>
                <tr style={{ background: '#000', color: '#FFF' }}>
                  <th style={{ width: '90px', minWidth: '90px', padding: '6px 2px', border: '1px solid #333' }}>Acc. / 🎨</th>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      style={{
                        width: `${col.width}px`,
                        minWidth: `${col.width}px`,
                        padding: '6px 3px',
                        border: '1px solid #444',
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                        textAlign: col.numeric ? 'right' : 'left'
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dayRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={COLUMNS.length + 1}
                      style={{
                        textAlign: 'center',
                        padding: '36px 16px',
                        backgroundColor: '#F8FAFC'
                      }}
                    >
                      <Stack spacing={1.5} alignItems="center" justifyContent="center">
                        <Typography variant="body2" sx={{ color: '#64748B', fontStyle: 'italic', fontSize: '13px' }}>
                          No hay citas registradas para este día.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => onInsertRow(dateStr, 0)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            fontSize: '13px',
                            px: 2.5,
                            py: 0.8,
                            bgcolor: '#10B981',
                            color: '#FFFFFF',
                            boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                            '&:hover': { bgcolor: '#059669' }
                          }}
                        >
                          + Añadir Cita
                        </Button>
                      </Stack>
                    </td>
                  </tr>
                ) : (
                  dayRows.map((row, idx) => {
                    const rowBg = row.color_fila || '#FFFFFF';
                    const isDragOver =
                      dragOverTarget &&
                      dragOverTarget.fecha === dateStr &&
                      dragOverTarget.idx === idx;

                    return (
                      <React.Fragment key={row.id || idx}>
                        {/* FILA HOVER SUPERIOR PARA INSERTAR (+) ARRIBA DE LA PRIMERA FILA */}
                        {idx === 0 && (
                          <tr>
                            <td
                              colSpan={COLUMNS.length + 1}
                              style={{ padding: 0, height: '6px', position: 'relative' }}
                            >
                              <Box
                                onClick={() => onInsertRow(dateStr, 0)}
                                sx={{
                                  height: '6px',
                                  backgroundColor: 'transparent',
                                  cursor: 'pointer',
                                  position: 'relative',
                                  '&:hover': {
                                    backgroundColor: '#3B82F6',
                                    '& .add-btn-top': { display: 'flex' }
                                  }
                                }}
                                title="Insertar nueva cita al inicio de este día"
                              >
                                <Box
                                  className="add-btn-top"
                                  sx={{
                                    display: 'none',
                                    position: 'absolute',
                                    left: '20px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    backgroundColor: '#2563EB',
                                    color: '#FFF',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 20
                                  }}
                                >
                                  <AddIcon style={{ fontSize: 16 }} />
                                </Box>
                              </Box>
                            </td>
                          </tr>
                        )}

                        <tr
                          style={{
                            backgroundColor: rowBg,
                            transition: 'all 0.15s ease',
                            borderBottom: '1px solid #CBD5E1',
                            borderTop: isDragOver ? '3px solid #2563EB' : 'none',
                            boxShadow: isDragOver ? '0 -3px 6px rgba(37, 99, 235, 0.4)' : 'none'
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            if (draggedRow && draggedRow.fecha === dateStr && dragOverTarget?.idx !== idx) {
                              setDragOverTarget({ fecha: dateStr, idx });
                            }
                          }}
                        >
                          {/* COLUMNA DE LA IZQUIERDA DEL TODO */}
                          <td
                            style={{
                              border: '1px solid #CBD5E1',
                              textAlign: 'center',
                              padding: '2px 4px'
                            }}
                          >
                            <Stack direction="row" spacing={0.3} alignItems="center" justifyContent="center">
                              {/* BOTÓN BORRAR FILA */}
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDeleteRow(row.id, dateStr)}
                                title="Eliminar esta fila"
                                sx={{ p: 0.3 }}
                              >
                                <DeleteIcon fontSize="small" sx={{ fontSize: '18px' }} />
                              </IconButton>

                              {/* BOTÓN CANCELAR / TOGGLE TEXTO ROJO */}
                              <IconButton
                                size="small"
                                onClick={() => onToggleCancelRow(dateStr, row.id, idx)}
                                title={row.datos?.cancelado ? 'Desmarcar cancelación (texto normal)' : 'Marcar como cancelada (texto rojo)'}
                                sx={{
                                  p: 0.3,
                                  color: row.datos?.cancelado ? '#DC2626' : '#94A3B8',
                                  '&:hover': { color: '#DC2626', backgroundColor: 'rgba(220, 38, 38, 0.08)' }
                                }}
                              >
                                <BlockIcon sx={{ fontSize: '18px' }} />
                              </IconButton>

                              {/* HANDLE DRAG AND DROP */}
                              <Box
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('text/plain', `${dateStr}_${idx}`);
                                  setDraggedRow({ fecha: dateStr, idx });
                                }}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  if (draggedRow && draggedRow.fecha === dateStr && dragOverTarget?.idx !== idx) {
                                    setDragOverTarget({ fecha: dateStr, idx });
                                  }
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  if (draggedRow && draggedRow.fecha === dateStr) {
                                    onDropRow(dateStr, draggedRow.idx, idx);
                                    setDraggedRow(null);
                                    setDragOverTarget(null);
                                  }
                                }}
                                sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', px: 0.2 }}
                              >
                                <DragIndicatorIcon
                                  sx={{ fontSize: 18, color: '#64748B', '&:hover': { color: '#2563EB' } }}
                                  titleAccess="Arrastra para mover fila"
                                />
                              </Box>

                              {/* BOTÓN COLOR DE FILA */}
                              <Box
                                onClick={(e) =>
                                  onOpenColorPicker(e, { type: 'row', fecha: dateStr, rowId: row.id })
                                }
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '3px',
                                  backgroundColor: row.color_fila || '#E2E8F0',
                                  border: '1px solid #94A3B8',
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                  '&:hover': { opacity: 0.8 }
                                }}
                                title="Cambiar color de fondo de fila"
                              />
                            </Stack>
                          </td>

                          {/* CELDAS EDITABLES DE LA FILA */}
                          {COLUMNS.map((col, colIdx) => {
                            const cellValue = row.datos?.[col.key] ?? '';
                            const cellColor = row.datos?.cell_colors?.[col.key] || null;

                            return (
                              <td
                                key={col.key}
                                className="agenda-cell"
                                style={{
                                  padding: '1px 2px',
                                  backgroundColor: cellColor || undefined,
                                  position: 'relative',
                                  width: `${col.width}px`,
                                  minWidth: `${col.width}px`
                                }}
                              >
                                {/* BOTÓN PARA COLOR DE CELDA */}
                                <Box
                                  className="cell-color-btn"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onOpenColorPicker(e, {
                                      type: 'cell',
                                      fecha: dateStr,
                                      rowId: row.id,
                                      colKey: col.key
                                    });
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    width: 14,
                                    height: 14,
                                    backgroundColor: cellColor || '#3B82F6',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    zIndex: 10,
                                    border: '1px solid #1D4ED8',
                                    boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                                    '&:hover': { transform: 'scale(1.25)' }
                                  }}
                                  title="Cambiar color de esta celda"
                                />

                                {renderCellInput(col, row, idx, cellValue, colIdx)}
                              </td>
                            );
                          })}
                        </tr>

                        {/* FILA HOVER INTERMEDIA PARA INSERTAR (+) A LA IZQUIERDA DEL TODO */}
                        <tr>
                          <td
                            colSpan={COLUMNS.length + 1}
                            style={{ padding: 0, height: '4px', position: 'relative' }}
                          >
                            <Box
                              onClick={() => onInsertRow(dateStr, idx + 1)}
                              sx={{
                                height: '6px',
                                backgroundColor: 'transparent',
                                cursor: 'pointer',
                                position: 'relative',
                                '&:hover': {
                                  backgroundColor: '#3B82F6',
                                  '& .add-btn': { display: 'flex' }
                                }
                              }}
                            >
                              <Box
                                className="add-btn"
                                sx={{
                                  display: 'none',
                                  position: 'absolute',
                                  left: '20px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  backgroundColor: '#2563EB',
                                  color: '#FFF',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 20
                                }}
                              >
                                <AddIcon style={{ fontSize: 16 }} />
                              </Box>
                            </Box>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>

              {/* FOOTER TOTALES DEL DÍA CON SOPORTE DE DROP AL FINAL DE LA TABLA */}
              <tfoot
                onDragOver={(e) => {
                  e.preventDefault();
                  if (draggedRow && draggedRow.fecha === dateStr && dragOverTarget?.idx !== dayRows.length) {
                    setDragOverTarget({ fecha: dateStr, idx: dayRows.length });
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggedRow && draggedRow.fecha === dateStr) {
                    onDropRow(dateStr, draggedRow.idx, dayRows.length);
                    setDraggedRow(null);
                    setDragOverTarget(null);
                  }
                }}
                style={{
                  background: '#F1F5F9',
                  borderTop:
                    dragOverTarget && dragOverTarget.fecha === dateStr && dragOverTarget.idx === dayRows.length
                      ? '3px solid #2563EB'
                      : '1px solid #CBD5E1'
                }}
              >
                {/* FILA DE CABECERA REPETIDA AL PIE DE LA TABLA ANTES DE LOS TOTALES */}
                <tr style={{ background: '#0F172A', color: '#FFF' }}>
                  <th style={{ width: '90px', minWidth: '90px', padding: '6px 2px', border: '1px solid #333' }}>Acc. / 🎨</th>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      style={{
                        width: `${col.width}px`,
                        minWidth: `${col.width}px`,
                        padding: '6px 3px',
                        border: '1px solid #444',
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                        textAlign: col.numeric ? 'right' : 'left'
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>

                {/* FILA DE TOTALES */}
                <tr style={{ background: '#F8FAFC', fontWeight: 'bold' }}>
                  <td style={{ border: '1px solid #CBD5E1', padding: '4px 2px', textAlign: 'center', fontSize: '12px' }}>TOTAL</td>
                  {COLUMNS.map((col) => {
                    let cellContent = '';
                    if (col.key === 'duration') {
                      cellContent = calculateTotalDuration(dayRows);
                    } else if (col.numeric) {
                      cellContent = `${calculateTotal(dayRows, col.key).toFixed(2)} €`;
                    }
                    return (
                      <td
                        key={col.key}
                        style={{
                          border: '1px solid #CBD5E1',
                          padding: '4px 2px',
                          fontSize: '12px',
                          textAlign: col.numeric || col.key === 'duration' ? 'right' : 'left'
                        }}
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </Box>

          {/* SECCIÓN REGISTROS ADICIONALES (6 COLUMNAS DE TEXTO LIBRE Y PRECIO) */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '2px dashed #CBD5E1' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1E293B', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  📝 Registros Adicionales / Venta de Cupones / Otros Pagos
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                  ({dayAdicionales.length} {dayAdicionales.length === 1 ? 'fila' : 'filas'})
                </Typography>
              </Stack>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => onAddAdicionalRow(dateStr)}
                sx={{
                  bgcolor: '#334155',
                  color: '#FFF',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '12px',
                  px: 1.5,
                  py: 0.5,
                  '&:hover': { bgcolor: '#1E293B' }
                }}
              >
                Añadir Fila Adicional
              </Button>
            </Box>

            <Box sx={{ overflowX: 'auto', borderRadius: 1.5, border: '1px solid #CBD5E1', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', backgroundColor: '#FFF' }}>
                <thead>
                  <tr style={{ background: '#334155', color: '#F8FAFC', textAlign: 'left' }}>
                    <th style={{ width: 45, textAlign: 'center', padding: '6px 4px' }}>#</th>
                    {ADICIONALES_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        style={{
                          width: col.width,
                          padding: '7px 8px',
                          fontWeight: 600,
                          fontSize: '12px',
                          letterSpacing: '0.5px',
                          textAlign: col.numeric ? 'right' : 'left'
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                    <th style={{ width: 45, textAlign: 'center', padding: '6px 4px' }} />
                  </tr>
                </thead>
                <tbody>
                  {dayAdicionales.map((row, aIdx) => {
                    const rowBg = row.color_fila || '#FFFFFF';
                    return (
                      <tr key={row.id || `temp-adicional-${aIdx}`} style={{ backgroundColor: rowBg, borderBottom: '1px solid #E2E8F0' }}>
                        <td style={{ textAlign: 'center', color: '#94A3B8', fontSize: '11px', fontWeight: 600, borderRight: '1px solid #E2E8F0', position: 'relative' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => onOpenColorPicker(e, { type: 'row', fecha: dateStr, rowId: row.id, isAdicional: true })}
                            sx={{ p: 0.3, color: '#94A3B8', '&:hover': { color: '#2563EB' } }}
                            title="Cambiar color de fila"
                          >
                            <span style={{ fontSize: '11px' }}>{aIdx + 1}</span>
                          </IconButton>
                        </td>
                        {ADICIONALES_COLUMNS.map((col) => {
                          const cellVal = row.datos ? row.datos[col.key] || '' : '';
                          const cellBg = row.datos?.cell_colors?.[col.key] || 'transparent';
                          return (
                            <td
                              key={col.key}
                              className="agenda-cell"
                              style={{
                                padding: '2px 4px',
                                borderRight: '1px solid #E2E8F0',
                                backgroundColor: cellBg !== 'transparent' ? cellBg : undefined,
                                position: 'relative'
                              }}
                            >
                              <CellInput
                                initialValue={cellVal}
                                numeric={col.numeric}
                                onCellBlur={(newVal) => onAdicionalCellBlurSave(dateStr, row.id, aIdx, col.key, newVal)}
                              />
                            </td>
                          );
                        })}
                        <td style={{ textAlign: 'center', padding: '2px' }}>
                          <IconButton
                            size="small"
                            onClick={() => onDeleteAdicionalRow(dateStr, row.id, aIdx)}
                            sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444' } }}
                            title="Eliminar fila"
                          >
                            <DeleteIcon fontSize="small" sx={{ fontSize: '16px' }} />
                          </IconButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot style={{ background: '#F8FAFC', fontWeight: 'bold', borderTop: '2px solid #CBD5E1' }}>
                  <tr>
                    <td style={{ textAlign: 'center', color: '#1E293B', fontSize: '12px', fontWeight: 'bold', borderRight: '1px solid #E2E8F0', padding: '6px 4px' }}>
                      TOTAL
                    </td>
                    {ADICIONALES_COLUMNS.map((col) => {
                      const totalVal = col.numeric ? `${calculateTotal(dayAdicionales, col.key).toFixed(2)} €` : '';
                      return (
                        <td
                          key={col.key}
                          style={{
                            padding: '6px 8px',
                            borderRight: '1px solid #E2E8F0',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            textAlign: col.numeric ? 'right' : 'left',
                            color: '#1E293B'
                          }}
                        >
                          {totalVal}
                        </td>
                      );
                    })}
                    <td style={{ borderRight: 'none', padding: '6px 4px' }} />
                  </tr>
                </tfoot>
              </table>
            </Box>

            {/* RESUMEN DE TOTALES DEL DÍA */}
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: 2,
                backgroundColor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 2
              }}
            >
              {/* Total Citas */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.2,
                  borderRadius: 1.5,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #CBD5E1',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Total Citas:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1E293B' }}>
                  {totalCitas.toFixed(2)} €
                </Typography>
              </Box>

              {/* Total Registros Adicionales */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.2,
                  borderRadius: 1.5,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #CBD5E1',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Total Reg. Adicionales:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1E293B' }}>
                  {totalAdicionales.toFixed(2)} €
                </Typography>
              </Box>

              {/* Total del Día */}
              <Box
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: 1.5,
                  backgroundColor: '#0F172A',
                  border: '1px solid #0F172A',
                  color: '#FFFFFF',
                  boxShadow: '0 2px 4px rgba(15,23,42,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Total del Día:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#38BDF8' }}>
                  {totalDia.toFixed(2)} €
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }
);

const getTodayDateStr = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

export default function CitasDia(props) {
  const usuario = workWithLocalStorage('get', 'usuario', null);

  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [selectedDateStr, setSelectedDateStr] = useState(getTodayDateStr());

  const [citasMap, setCitasMap] = useState({});
  const [personalMap, setPersonalMap] = useState({});
  const [adicionalesMap, setAdicionalesMap] = useState({});
  const [notasMap, setNotasMap] = useState({});
  const [clientesList, setClientesList] = useState([]);
  const [isLoadingMonth, setIsLoadingMonth] = useState(true);
  const clientsLoadedRef = useRef(false);

  // Estado para arrastrar y soltar filas (Drag and Drop)
  const [draggedRow, setDraggedRow] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);

  // Modal para alta cliente
  const [modalClienteOpen, setModalClienteOpen] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState({ nombre: '', apellidos: '', telefono: '' });
  const [pendingCellTarget, setPendingCellTarget] = useState(null);

  // Estado de contracción/expansión del personal para cada día (expandido por defecto)
  const [collapsedPersonalMap, setCollapsedPersonalMap] = useState({});

  const togglePersonalCollapse = useCallback((dateStr) => {
    setCollapsedPersonalMap((prev) => ({
      ...prev,
      [dateStr]: prev[dateStr] === undefined ? true : !prev[dateStr]
    }));
  }, []);

  const [modalPersonalOpen, setModalPersonalOpen] = useState(false);
  const [nuevoPersonal, setNuevoPersonal] = useState({ fecha: '', nombre: '', tipo_personal: 'TERAPEUTA', estado_horario: '' });

  // Popovers de color
  const [colorPopoverAnchor, setColorPopoverAnchor] = useState(null);
  const [colorPopoverTarget, setColorPopoverTarget] = useState(null);

  // Popover de selección de fecha
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const [pickerMonth, setPickerMonth] = useState(new Date().getMonth() + 1);

  const citasMapRef = useRef(citasMap);
  citasMapRef.current = citasMap;

  const personalMapRef = useRef(personalMap);
  personalMapRef.current = personalMap;

  const adicionalesMapRef = useRef(adicionalesMap);
  adicionalesMapRef.current = adicionalesMap;

  const notasMapRef = useRef(notasMap);
  notasMapRef.current = notasMap;

  // DISTINCT DE SERVICIOS UNICOS PARA EL DESPLEGABLE
  const serviciosUnicos = useMemo(() => {
    const map = new Map();
    (props.servicios || []).forEach((s) => {
      if (s && s.nombre) {
        const nameClean = s.nombre.trim();
        if (nameClean && !map.has(nameClean.toLowerCase())) {
          map.set(nameClean.toLowerCase(), { id: s.id, nombre: nameClean });
        }
      }
    });
    return Array.from(map.values());
  }, [props.servicios]);

  const propsRef = useRef(props);
  propsRef.current = props;
  const isFetchingMesRef = useRef(false);

  // Carga de datos del mes (función 100% estable)
  const cargarMes = useCallback(async (year, month) => {
    if (isFetchingMesRef.current) return;
    isFetchingMesRef.current = true;
    try {
      setIsLoadingMonth(true);
      if (propsRef.current.showLoader) propsRef.current.showLoader(true);
      const res = await fetch(`${CONFIG.api}api/citas/mes/${year}/${month}`);
      const result = await res.json();

      const cMap = {};
      const pMap = {};
      const aMap = {};
      const nMap = {};

      if (result.error === 0) {
        const { citas, personal, adicionales, notas } = result.data;
        (citas || []).forEach((row) => {
          let parsedDatos = {};
          try {
            parsedDatos = typeof row.datos === 'string' ? JSON.parse(row.datos) : (row.datos || {});
          } catch (e) {
            parsedDatos = {};
          }
          if (!cMap[row.fecha]) cMap[row.fecha] = [];
          cMap[row.fecha].push({
            id: row.id,
            fecha: row.fecha,
            orden: row.orden,
            color_fila: row.color_fila || '',
            cliente_id: row.cliente_id,
            servicio_id: row.servicio_id,
            room_id: row.room_id,
            datos: parsedDatos
          });
        });

        // Ordenar cada día por Time y Room en orden ascendente
        Object.keys(cMap).forEach((f) => {
          cMap[f] = sortCitasByTimeAndRoom(cMap[f]);
        });

        (personal || []).forEach((p) => {
          if (!pMap[p.fecha]) pMap[p.fecha] = [];
          pMap[p.fecha].push(p);
        });

        (adicionales || []).forEach((row) => {
          let parsedDatos = {};
          try {
            parsedDatos = typeof row.datos === 'string' ? JSON.parse(row.datos) : (row.datos || {});
          } catch (e) {
            parsedDatos = {};
          }
          if (!aMap[row.fecha]) aMap[row.fecha] = [];
          aMap[row.fecha].push({
            id: row.id,
            fecha: row.fecha,
            orden: row.orden,
            color_fila: row.color_fila || '',
            datos: parsedDatos
          });
        });

        (notas || []).forEach((row) => {
          if (row.fecha) {
            nMap[row.fecha] = row.nota || '';
          }
        });
      }

      setCitasMap(cMap);
      setPersonalMap(pMap);
      setAdicionalesMap(aMap);
      setNotasMap(nMap);
      setIsLoadingMonth(false);
      if (propsRef.current.showLoader) propsRef.current.showLoader(false);

      if (!clientsLoadedRef.current) {
        const u = workWithLocalStorage('get', 'usuario', null);
        const tokenVal = u ? u.token : '';
        getClientes(tokenVal).then((cls) => {
          const uniqueClientsMap = new Map();
          (cls || []).forEach((c) => {
            if (c && (c.nombre || c.apellidos)) {
              const fullName = `${c.nombre || ''} ${c.apellidos || ''}`.trim();
              const key = `${fullName}_${c.id}`.toLowerCase();
              if (!uniqueClientsMap.has(key)) {
                let isActivo = 1;
                if (c.activo !== undefined && c.activo !== null) {
                  isActivo = Number(c.activo);
                } else if (c.estado === 'Borrado') {
                  isActivo = 0;
                }

                uniqueClientsMap.set(key, {
                  id: c.id,
                  nombre: c.nombre || '',
                  apellidos: c.apellidos || '',
                  telefono: c.telefono || '',
                  activo: isActivo,
                  estado: c.estado || '',
                  label: fullName
                });
              }
            }
          });
          setClientesList(Array.from(uniqueClientsMap.values()));
          clientsLoadedRef.current = true;
        }).catch((e) => console.error('Error al cargar clientes:', e));
      }
    } catch (err) {
      console.error('Error al cargar mes:', err);
      setIsLoadingMonth(false);
      if (propsRef.current.showLoader) propsRef.current.showLoader(false);
    } finally {
      isFetchingMesRef.current = false;
    }
  }, []);

  useEffect(() => {
    cargarMes(currentYear, currentMonth);

    const handleRefreshEvent = () => {
      cargarMes(currentYear, currentMonth);
    };

    window.addEventListener('agenda_refresh_needed', handleRefreshEvent);

    let channel = null;
    try {
      channel = new BroadcastChannel('agenda_sync_channel');
      channel.onmessage = (event) => {
        if (event && event.data && event.data.type === 'REFRESH_AGENDA') {
          cargarMes(currentYear, currentMonth);
        }
      };
    } catch (e) {
      // BroadcastChannel no soportado
    }

    return () => {
      window.removeEventListener('agenda_refresh_needed', handleRefreshEvent);
      if (channel) channel.close();
    };
  }, [currentYear, currentMonth, cargarMes]);

  const handlePrevMonth = () => {
    let nextMonth = currentMonth - 1;
    let nextYear = currentYear;
    if (nextMonth < 1) {
      nextMonth = 12;
      nextYear = currentYear - 1;
    }
    setCurrentMonth(nextMonth);
    setCurrentYear(nextYear);
    const firstDay = `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;
    setSelectedDateStr(firstDay);
    cargarMes(nextYear, nextMonth);
  };

  const handleNextMonth = () => {
    let nextMonth = currentMonth + 1;
    let nextYear = currentYear;
    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear = currentYear + 1;
    }
    setCurrentMonth(nextMonth);
    setCurrentYear(nextYear);
    const firstDay = `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;
    setSelectedDateStr(firstDay);
    cargarMes(nextYear, nextMonth);
  };

  // GUARDADO DE CELDA AL DESENFOCAR
  const handleCellBlurSave = useCallback(async (fecha, rowId, rowIndex, colKey, value) => {
    const dayRows = [...(citasMapRef.current[fecha] || [])];
    let row = rowId ? dayRows.find((r) => r.id === rowId) : dayRows[rowIndex];

    if (!row) {
      if (!value || String(value).trim() === '' || value === 0 || value === '0') {
        return;
      }
      row = {
        id: null,
        fecha,
        orden: dayRows.length,
        color_fila: '',
        cliente_id: null,
        servicio_id: null,
        room_id: null,
        datos: {}
      };
      dayRows.push(row);
    }

    const currentVal = row.datos ? row.datos[colKey] : undefined;
    const isSame = (currentVal === value) ||
      (String(currentVal ?? '').trim() === String(value ?? '').trim() && (currentVal !== undefined || value !== ''));

    if (isSame) {
      return;
    }

    row.datos = { ...(row.datos || {}), [colKey]: value };

    if (colKey === 'treatment') {
      const srv = (props.servicios || []).find((s) => s.nombre === value || s.id === value);
      if (srv) row.servicio_id = srv.id;
    }
    if (colKey === 'room') {
      const rm = (props.rooms || []).find((r) => r.codigo === value || r.nombre === value || r.id === value);
      if (rm) row.room_id = rm.id;
    }

    // Comprobar si la fila está completamente vacía
    if (isRowEmpty(row)) {
      if (row.id) {
        fetch(`${CONFIG.api}api/citas/delete-row/${row.id}`, { method: 'DELETE' }).catch((e) => console.error(e));
        row.id = null;
      }
      const filtered = dayRows.filter((r) => !isRowEmpty(r) || r.id);
      const sorted = sortCitasByTimeAndRoom(filtered);
      sorted.forEach((r, idx) => { r.orden = idx; });
      setCitasMap({ ...citasMapRef.current, [fecha]: sorted });
      return;
    }

    const sorted = sortCitasByTimeAndRoom(dayRows);
    sorted.forEach((r, idx) => { r.orden = idx; });
    setCitasMap({ ...citasMapRef.current, [fecha]: sorted });

    try {
      const res = await fetch(`${CONFIG.api}api/citas/upsert-row`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(row)
      });
      const result = await res.json();
      if (result.error === 0 && result.data && result.data.id) {
        if (!row.id) {
          row.id = result.data.id;
          const updated = [...(citasMapRef.current[fecha] || [])];
          setCitasMap({ ...citasMapRef.current, [fecha]: sortCitasByTimeAndRoom(updated) });
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [props.servicios, props.rooms]);

  // DRAG AND DROP: REORDENAR FILAS DENTRO DE LA MISMA FECHA
  const handleDropRow = useCallback((fecha, fromIndex, toIndex) => {
    const dayRows = [...(citasMapRef.current[fecha] || [])];
    if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= dayRows.length) return;

    const [movedRow] = dayRows.splice(fromIndex, 1);

    let insertPos = toIndex;
    if (toIndex > fromIndex) {
      insertPos = toIndex > dayRows.length ? dayRows.length : toIndex;
    }
    dayRows.splice(insertPos, 0, movedRow);

    dayRows.forEach((r, idx) => {
      r.orden = idx;
    });

    setCitasMap({ ...citasMapRef.current, [fecha]: dayRows });

    const orderedIds = dayRows.map((r) => r.id).filter((id) => id !== null && id !== undefined);
    if (orderedIds.length > 0) {
      fetch(`${CONFIG.api}api/citas/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fecha, orderedIds })
      }).catch((e) => console.error('Error al reordenar filas:', e));
    }
  }, []);

  // Selección de cliente autocompletado
  const handleSelectClient = useCallback(async (fecha, rowId, rowIndex, selectedClient) => {
    if (!selectedClient) return;

    if (selectedClient.activo === 0 || selectedClient.estado === 'Borrado') {
      Swal.fire({
        title: '⚠️ Cliente Desactivado',
        text: `El cliente "${selectedClient.label || selectedClient.nombre}" se encuentra en estado DADO DE BAJA / BORRADO en el sistema.`,
        icon: 'warning',
        confirmButtonText: 'Entendido'
      });
    }

    const dayRows = [...(citasMapRef.current[fecha] || [])];
    const row = dayRows.find((r) => r.id === rowId) || dayRows[rowIndex];
    if (!row) return;

    const fullName = `${selectedClient.nombre || ''} ${selectedClient.apellidos || ''}`.trim();
    row.cliente_id = selectedClient.id;
    row.datos = {
      ...(row.datos || {}),
      name: fullName,
      telephone: selectedClient.telefono || ''
    };

    const sorted = sortCitasByTimeAndRoom(dayRows);
    sorted.forEach((r, idx) => { r.orden = idx; });
    setCitasMap({ ...citasMapRef.current, [fecha]: sorted });

    try {
      const res = await fetch(`${CONFIG.api}api/citas/upsert-row`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(row)
      });
      const result = await res.json();
      if (result.error === 0 && result.data && result.data.id) {
        if (!row.id) {
          row.id = result.data.id;
          const updated = [...(citasMapRef.current[fecha] || [])];
          setCitasMap({ ...citasMapRef.current, [fecha]: sortCitasByTimeAndRoom(updated) });
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Manejar cuando se escribe un nombre de cliente manualmente
  const handleNameBlur = useCallback((fecha, rowId, rowIndex, typedValue) => {
    const dayRows = [...(citasMapRef.current[fecha] || [])];
    const row = dayRows.find((r) => r.id === rowId) || dayRows[rowIndex];
    if (!row) return;

    const prevName = String(row.datos?.name || '').trim().toLowerCase();
    const newName = String(typedValue || '').trim().toLowerCase();
    if (prevName === newName) {
      return;
    }

    if (!typedValue || typedValue.trim().length === 0) {
      row.datos = { ...(row.datos || {}), name: '' };
      row.cliente_id = null;
      if (isRowEmpty(row) && row.id) {
        fetch(`${CONFIG.api}api/citas/delete-row/${row.id}`, { method: 'DELETE' }).catch((e) => console.error(e));
        row.id = null;
      } else if (row.id) {
        fetch(`${CONFIG.api}api/citas/upsert-row`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(row)
        }).catch((e) => console.error(e));
      }
      const filtered = dayRows.filter((r) => !isRowEmpty(r) || r.id);
      const sorted = sortCitasByTimeAndRoom(filtered);
      sorted.forEach((r, idx) => { r.orden = idx; });
      setCitasMap({ ...citasMapRef.current, [fecha]: sorted });
      return;
    }

    handleCellBlurSave(fecha, rowId, rowIndex, 'name', typedValue);

    const typedClean = normalizeText(typedValue);
    const matchedClient = clientesList.find((c) => {
      const cFull = normalizeText(c.label || `${c.nombre || ''} ${c.apellidos || ''}`);
      const cNom = normalizeText(c.nombre);
      const cApe = normalizeText(c.apellidos);
      return cFull === typedClean || (cNom && cNom === typedClean) || (cApe && cApe === typedClean);
    });

    if (matchedClient) {
      row.cliente_id = matchedClient.id;
      if (!row.datos?.telephone && matchedClient.telefono) {
        row.datos = { ...(row.datos || {}), telephone: matchedClient.telefono };
      }
      const sorted = sortCitasByTimeAndRoom(dayRows);
      sorted.forEach((r, idx) => { r.orden = idx; });
      setCitasMap({ ...citasMapRef.current, [fecha]: sorted });
      fetch(`${CONFIG.api}api/citas/upsert-row`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(row)
      }).catch((e) => console.error(e));
      return;
    }

    row.cliente_id = null;
    setPendingCellTarget({ fecha, rowId, rowIndex, typedValue });
    const parts = typedValue.trim().split(' ');
    setNuevoCliente({
      nombre: parts[0] || '',
      apellidos: parts.slice(1).join(' ') || '',
      telefono: ''
    });
    setModalClienteOpen(true);
  }, [clientesList, handleCellBlurSave]);

  const handleSaveNuevoCliente = async () => {
    if (!nuevoCliente.nombre || !nuevoCliente.apellidos || !nuevoCliente.telefono) {
      Swal.fire('Atención', 'Nombre, Apellidos y Teléfono son obligatorios.', 'warning');
      return;
    }

    try {
      const bodyJson = JSON.stringify(buildBody(usuario.token, nuevoCliente));
      const res = await fetch(`${CONFIG.api}cliente/detalle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: bodyJson
      });
      const result = await res.json();

      if (result.error === 0 && result.data && result.data.id) {
        const fullName = `${nuevoCliente.nombre} ${nuevoCliente.apellidos}`.trim();
        const clientCreated = {
          id: result.data.id,
          ...nuevoCliente,
          activo: 1,
          label: fullName
        };
        setClientesList((prev) => [...prev, clientCreated]);

        if (pendingCellTarget) {
          handleSelectClient(
            pendingCellTarget.fecha,
            pendingCellTarget.rowId,
            pendingCellTarget.rowIndex,
            clientCreated
          );
        }
        setModalClienteOpen(false);
        setPendingCellTarget(null);
        Swal.fire('Éxito', 'Cliente registrado correctamente.', 'success');
      } else {
        Swal.fire('Error', result.errorMessage || 'Error al guardar cliente', 'error');
      }
    } catch (e) {
      console.error(e);
      Swal.fire('Error', 'No se pudo registrar el cliente.', 'error');
    }
  };

  const handleInsertRow = useCallback(async (fecha, ordenPos) => {
    try {
      const res = await fetch(`${CONFIG.api}api/citas/insert-row`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fecha, orden: ordenPos })
      });
      const result = await res.json();
      const newId = result.error === 0 && result.data ? result.data.id : null;

      const dayRows = [...(citasMapRef.current[fecha] || [])];
      const newRow = {
        id: newId,
        fecha,
        orden: ordenPos,
        color_fila: '',
        cliente_id: null,
        servicio_id: null,
        room_id: null,
        datos: {
          time: '', cf: '', room: '', ther: '', recep: '', name: '', telephone: '',
          treatment: '', duration: '', comment: '', scrub: '', oil: '', ticket_no: '',
          cash: 0, visa: 0, transfer: 0, treatmens: '', cell_colors: {}
        }
      };

      dayRows.splice(ordenPos, 0, newRow);
      dayRows.forEach((r, i) => { r.orden = i; });
      setCitasMap({ ...citasMapRef.current, [fecha]: dayRows });
    } catch (e) {
      console.error('Error al insertar fila intermedia:', e);
    }
  }, []);

  const handleDeleteRow = useCallback(async (rowId, fecha) => {
    try {
      const confirm = await Swal.fire({
        title: '¿Eliminar fila?',
        text: 'Esta acción no se puede deshacer',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar'
      });
      if (confirm.isConfirmed) {
        if (rowId) {
          await fetch(`${CONFIG.api}api/citas/delete-row/${rowId}`, { method: 'DELETE' });
        }
        if (fecha && citasMapRef.current[fecha]) {
          const updated = (citasMapRef.current[fecha] || []).filter((r) => r.id !== rowId);
          const sorted = sortCitasByTimeAndRoom(updated);
          sorted.forEach((r, idx) => { r.orden = idx; });
          setCitasMap({ ...citasMapRef.current, [fecha]: sorted });
          const orderedIds = sorted.map((r) => r.id).filter((id) => id !== null && id !== undefined);
          if (orderedIds.length > 0) {
            fetch(`${CONFIG.api}api/citas/reorder`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ fecha, orderedIds })
            }).catch((e) => console.error(e));
          }
        } else {
          cargarMes(currentYear, currentMonth);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [currentYear, currentMonth, cargarMes]);

  const handleToggleCancelRow = useCallback((fecha, rowId, rowIndex) => {
    const dayRows = [...(citasMapRef.current[fecha] || [])];
    const row = rowId ? dayRows.find((r) => r.id === rowId) : dayRows[rowIndex];
    if (!row) return;

    const currentCancelled = !!row.datos?.cancelado;
    row.datos = { ...(row.datos || {}), cancelado: !currentCancelled };

    setCitasMap({ ...citasMapRef.current, [fecha]: dayRows });

    if (!isRowEmpty(row)) {
      fetch(`${CONFIG.api}api/citas/upsert-row`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(row)
      })
        .then((res) => res.json())
        .then((result) => {
          if (result.error === 0 && result.data && result.data.id) {
            row.id = result.data.id;
          }
        })
        .catch((e) => console.error(e));
    }
  }, []);

  const handleOpenColorPicker = useCallback((event, targetInfo) => {
    event.stopPropagation();
    event.preventDefault();
    setColorPopoverAnchor(event.currentTarget);
    setColorPopoverTarget(targetInfo);
  }, []);

  const handleSelectColor = (colorValue) => {
    if (!colorPopoverTarget) return;

    const { type, fecha, rowId, colKey, isAdicional } = colorPopoverTarget;

    if (isAdicional) {
      const dayRows = [...(adicionalesMapRef.current[fecha] || [])];
      const row = dayRows.find((r) => r.id === rowId);
      if (row) {
        if (type === 'row') {
          row.color_fila = colorValue;
        } else if (type === 'cell') {
          const cellColors = { ...(row.datos?.cell_colors || {}) };
          if (colorValue && colorValue !== '#FFFFFF') {
            cellColors[colKey] = colorValue;
          } else {
            delete cellColors[colKey];
          }
          row.datos = { ...(row.datos || {}), cell_colors: cellColors };
        }

        if (!isAdicionalRowEmpty(row)) {
          fetch(`${CONFIG.api}api/citas/upsert-adicional`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(row)
          }).then(res => res.json()).then(result => {
            if (result.error === 0 && result.data && result.data.id) {
              row.id = result.data.id;
            }
          }).catch((e) => console.error(e));
        }

        setAdicionalesMap({ ...adicionalesMapRef.current, [fecha]: ensureTrailingBlankAdicional(dayRows, fecha) });
      }
      setColorPopoverAnchor(null);
      setColorPopoverTarget(null);
      return;
    }

    const dayRows = [...(citasMapRef.current[fecha] || [])];
    const row = dayRows.find((r) => r.id === rowId);

    if (row) {
      if (type === 'row') {
        row.color_fila = colorValue;
      } else if (type === 'cell') {
        const cellColors = { ...(row.datos?.cell_colors || {}) };
        if (colorValue && colorValue !== '#FFFFFF') {
          cellColors[colKey] = colorValue;
        } else {
          delete cellColors[colKey];
        }
        row.datos = { ...(row.datos || {}), cell_colors: cellColors };
      }

      if (!isRowEmpty(row)) {
        fetch(`${CONFIG.api}api/citas/upsert-row`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(row)
        }).then(res => res.json()).then(result => {
          if (result.error === 0 && result.data && result.data.id) {
            row.id = result.data.id;
          }
        }).catch((e) => console.error(e));
      }

      setCitasMap({ ...citasMapRef.current, [fecha]: dayRows });
    }

    setColorPopoverAnchor(null);
    setColorPopoverTarget(null);
  };

  const handleAddAdicionalRow = useCallback(async (fecha, insertPos = null) => {
    const dayRows = [...(adicionalesMapRef.current[fecha] || [])];
    const pos = insertPos !== null && insertPos !== undefined ? insertPos : dayRows.length;
    try {
      const res = await fetch(`${CONFIG.api}api/citas/insert-adicional`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fecha, orden: pos })
      });
      const result = await res.json();
      const newId = result.error === 0 && result.data ? result.data.id : null;

      const newRow = {
        id: newId,
        fecha,
        orden: pos,
        color_fila: '',
        datos: { personal: '', concepto: '', pax_cliente: '', codigo_ref: '', forma_pago: '', precio: 0, cell_colors: {} }
      };
      dayRows.splice(pos, 0, newRow);
      dayRows.forEach((r, idx) => {
        r.orden = idx;
      });
      setAdicionalesMap({ ...adicionalesMapRef.current, [fecha]: ensureTrailingBlankAdicional(dayRows, fecha) });
    } catch (e) {
      console.error('Error al insertar fila adicional:', e);
    }
  }, []);

  const handleDeleteAdicionalRow = useCallback(async (fecha, rowId, rowIndex) => {
    const dayRows = [...(adicionalesMapRef.current[fecha] || [])];
    if (dayRows.length === 0) return;
    if (rowIndex >= 0 && rowIndex < dayRows.length) {
      const [deleted] = dayRows.splice(rowIndex, 1);
      dayRows.forEach((r, idx) => {
        r.orden = idx;
      });
      setAdicionalesMap({ ...adicionalesMapRef.current, [fecha]: ensureTrailingBlankAdicional(dayRows, fecha) });

      if (rowId || deleted?.id) {
        try {
          await fetch(`${CONFIG.api}api/citas/delete-adicional/${rowId || deleted.id}`, { method: 'DELETE' });
          const orderedIds = dayRows.map((r) => r.id).filter((id) => id !== null && id !== undefined);
          if (orderedIds.length > 0) {
            fetch(`${CONFIG.api}api/citas/reorder-adicional`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ fecha, orderedIds })
            }).catch((e) => console.error(e));
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, []);

  const handleAdicionalCellBlurSave = useCallback(async (fecha, rowId, rowIndex, colKey, value) => {
    const dayRows = [...(adicionalesMapRef.current[fecha] || [])];
    let row = rowId ? dayRows.find((r) => r.id === rowId) : dayRows[rowIndex];

    if (!row) {
      if (!value || String(value).trim() === '') return;
      row = {
        id: null,
        fecha,
        orden: dayRows.length,
        color_fila: '',
        datos: {}
      };
      dayRows.push(row);
    }

    const currentVal = row.datos ? row.datos[colKey] : undefined;
    if (String(currentVal ?? '').trim() === String(value ?? '').trim()) {
      return;
    }

    row.datos = { ...(row.datos || {}), [colKey]: value };

    if (isAdicionalRowEmpty(row)) {
      if (row.id) {
        fetch(`${CONFIG.api}api/citas/delete-adicional/${row.id}`, { method: 'DELETE' }).catch((e) => console.error(e));
        row.id = null;
      }
      const filtered = dayRows.filter((r) => !isAdicionalRowEmpty(r) || r.id);
      setAdicionalesMap({ ...adicionalesMapRef.current, [fecha]: ensureTrailingBlankAdicional(filtered, fecha) });
      return;
    }

    const updatedRows = ensureTrailingBlankAdicional(dayRows, fecha);
    setAdicionalesMap({ ...adicionalesMapRef.current, [fecha]: updatedRows });

    try {
      const res = await fetch(`${CONFIG.api}api/citas/upsert-adicional`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(row)
      });
      const result = await res.json();
      if (result.error === 0 && result.data && result.data.id) {
        if (!row.id) {
          row.id = result.data.id;
          setAdicionalesMap({ ...adicionalesMapRef.current, [fecha]: ensureTrailingBlankAdicional(dayRows, fecha) });
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handlePersonalBlurSave = useCallback((fecha, pItem, newEstado) => {
    if (String(pItem.estado_horario ?? '').trim() === String(newEstado ?? '').trim()) {
      return;
    }
    const pList = [...(personalMapRef.current[fecha] || [])];
    const item = pList.find((p) => (p.id ? p.id === pItem.id : p.nombre === pItem.nombre)) || pItem;
    item.estado_horario = newEstado;

    setPersonalMap({ ...personalMapRef.current, [fecha]: pList });

    fetch(`${CONFIG.api}api/citas/upsert-personal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item)
    }).catch((e) => console.error(e));
  }, []);

  const handleNotaSave = useCallback(async (fecha, nuevaNota) => {
    setNotasMap((prev) => ({ ...prev, [fecha]: nuevaNota }));
    try {
      await fetch(`${CONFIG.api}api/citas/upsert-nota`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fecha, nota: nuevaNota })
      });
    } catch (err) {
      console.error('Error guardando nota del día:', err);
    }
  }, []);

  const handleOpenAddPersonal = useCallback((fecha) => {
    setNuevoPersonal({ fecha, nombre: '', tipo_personal: 'TERAPEUTA', estado_horario: '' });
    setModalPersonalOpen(true);
  }, []);

  const handleSaveNuevoPersonal = async () => {
    if (!nuevoPersonal.nombre || !nuevoPersonal.fecha) {
      Swal.fire('Atención', 'El nombre de la persona es obligatorio.', 'warning');
      return;
    }

    try {
      const res = await fetch(`${CONFIG.api}api/citas/upsert-personal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nuevoPersonal)
      });
      const result = await res.json();
      if (result.error === 0) {
        setModalPersonalOpen(false);
        cargarMes(currentYear, currentMonth);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const scrollToDate = (targetDateStr) => {
    if (!targetDateStr) return;
    const [tY, tM] = targetDateStr.split('-').map((v) => parseInt(v, 10));
    setSelectedDateStr(targetDateStr);
    if (tY !== currentYear || tM !== currentMonth) {
      setCurrentYear(tY);
      setCurrentMonth(tM);
      cargarMes(tY, tM);
    }
  };

  const daysList = useMemo(() => getDaysInMonth(currentYear, currentMonth), [currentYear, currentMonth]);

  // Obtener datos del día seleccionado para renderizar exclusivamente su tarjeta
  const selectedDayData = useMemo(() => {
    const parts = (selectedDateStr || getTodayDateStr()).split('-');
    const sY = parseInt(parts[0], 10) || currentYear;
    const sM = parseInt(parts[1], 10) || currentMonth;
    const sD = parseInt(parts[2], 10) || 1;
    const dObj = new Date(sY, sM - 1, sD);
    const dayName = dObj.toLocaleDateString('es-ES', { weekday: 'long' }).toUpperCase();
    return {
      dateStr: selectedDateStr,
      dayName,
      dayNum: sD
    };
  }, [selectedDateStr, currentYear, currentMonth]);

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <style>{`
        .agenda-cell {
          border: 1px solid #CBD5E1;
          transition: border-color 0.1s ease, box-shadow 0.1s ease;
        }
        .agenda-cell:focus-within {
          border: 1.5px solid #2563EB !important;
          box-shadow: inset 0 0 0 1px #2563EB;
        }
        .cell-color-btn {
          display: none !important;
        }
        .agenda-cell:hover .cell-color-btn,
        .agenda-cell:focus-within .cell-color-btn {
          display: block !important;
        }
      `}</style>

      {/* NAVEGADOR SUPERIOR DE MES Y DÍA */}
      <Card sx={{ mb: 3, p: 2, background: '#1E293B', color: '#FFF' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={handlePrevMonth}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Mes Anterior
            </Button>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNextMonth}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Mes Siguiente
            </Button>
          </Stack>

          <Typography variant="h4" sx={{ fontWeight: 'bold', letterSpacing: 1.2, textAlign: 'center' }}>
            📅 {MESES[currentMonth - 1]} {currentYear}
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="contained"
              onClick={(e) => {
                setPickerYear(currentYear);
                setPickerMonth(currentMonth);
                setCalendarAnchorEl(e.currentTarget);
              }}
              sx={{
                bgcolor: '#334155',
                color: '#FFF',
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '13px',
                px: 2,
                py: 0.8,
                border: '1px solid #475569',
                boxShadow: 2,
                '&:hover': { bgcolor: '#475569', borderColor: '#64748B' }
              }}
            >
              📅 Seleccionar Día
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const todayStr = getTodayDateStr();
                scrollToDate(todayStr);
              }}
              sx={{
                borderColor: '#3B82F6',
                color: '#60A5FA',
                fontWeight: 'bold',
                textTransform: 'none',
                py: 0.8,
                px: 1.5,
                '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.1)', borderColor: '#60A5FA' }
              }}
            >
              Hoy
            </Button>
          </Stack>
        </Stack>

        {/* SELECTOR RÁPIDO DE DÍAS DEL MES EN CHIPS */}
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #334155', display: 'flex', flexWrap: 'wrap', gap: 0.6, alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 'bold', mr: 1 }}>
            Días:
          </Typography>
          {daysList.map(({ dateStr, dayNum, dayName }) => {
            const isSelected = dateStr === selectedDateStr;
            const todayStr = getTodayDateStr();
            const isToday = dateStr === todayStr;

            let chipBg = '#334155';
            let chipHoverBg = '#475569';
            let chipFontWeight = 500;
            let chipBorder = '1px solid transparent';

            if (isSelected) {
              chipBg = '#2563EB';
              chipHoverBg = '#1D4ED8';
              chipFontWeight = 800;
              chipBorder = '1.5px solid #60A5FA';
            } else if (isToday) {
              chipBg = '#1E40AF';
              chipHoverBg = '#1D4ED8';
              chipFontWeight = 700;
              chipBorder = '1px dashed #93C5FD';
            }

            return (
              <Box
                key={dateStr}
                onClick={() => scrollToDate(dateStr)}
                sx={{
                  px: 1.2,
                  py: 0.5,
                  borderRadius: '6px',
                  bgcolor: chipBg,
                  color: '#FFF',
                  fontSize: '11.5px',
                  fontWeight: chipFontWeight,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.15s ease',
                  border: chipBorder,
                  boxShadow: isSelected ? '0 0 8px rgba(37, 99, 235, 0.5)' : 'none',
                  '&:hover': { bgcolor: chipHoverBg, transform: 'scale(1.05)' }
                }}
                title={`Ver citas del ${dayName} ${dayNum}`}
              >
                {dayNum} {dayName.slice(0, 3)}
              </Box>
            );
          })}
        </Box>
      </Card>

      {/* VISTA EXCLUSIVA DEL DÍA SELECCIONADO */}
      {(() => {
        const { dateStr, dayName, dayNum } = selectedDayData;
        const dayRows = sortCitasByTimeAndRoom(citasMap[dateStr] || []);
        const dayPersonal = personalMap[dateStr] || [];
        const dayAdicionales = ensureTrailingBlankAdicional(adicionalesMap[dateStr] || [], dateStr);
        const dayNota = notasMap[dateStr] || '';
        const isPersonalCollapsed = collapsedPersonalMap[dateStr] === true;

        return (
          <DiaCard
            key={dateStr}
            dateStr={dateStr}
            dayName={dayName}
            dayNum={dayNum}
            dayRows={dayRows}
            dayPersonal={dayPersonal}
            dayAdicionales={dayAdicionales}
            dayNota={dayNota}
            onNotaSave={handleNotaSave}
            isPersonalCollapsed={isPersonalCollapsed}
            draggedRow={draggedRow}
            dragOverTarget={dragOverTarget}
            clientesList={clientesList}
            serviciosUnicos={serviciosUnicos}
            rooms={props.rooms}
            onInsertRow={handleInsertRow}
            onDeleteRow={handleDeleteRow}
            onToggleCancelRow={handleToggleCancelRow}
            onDropRow={handleDropRow}
            onOpenColorPicker={handleOpenColorPicker}
            onCellBlurSave={handleCellBlurSave}
            onSelectClient={handleSelectClient}
            onNameBlur={handleNameBlur}
            onAddAdicionalRow={handleAddAdicionalRow}
            onDeleteAdicionalRow={handleDeleteAdicionalRow}
            onAdicionalCellBlurSave={handleAdicionalCellBlurSave}
            onPersonalSave={handlePersonalBlurSave}
            onOpenAddPersonal={handleOpenAddPersonal}
            togglePersonalCollapse={togglePersonalCollapse}
            onRefreshDay={() => cargarMes(currentYear, currentMonth)}
            setDraggedRow={setDraggedRow}
            setDragOverTarget={setDragOverTarget}
          />
        );
      })()}

      {/* POPOVER SELECCIÓN DE COLOR */}
      <Popover
        open={Boolean(colorPopoverAnchor)}
        anchorEl={colorPopoverAnchor}
        onClose={() => {
          setColorPopoverAnchor(null);
          setColorPopoverTarget(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 260 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>
            🎨 Elige Color {colorPopoverTarget?.type === 'cell' ? 'de Celda' : 'de Fila'}
          </Typography>
          <Grid container spacing={1}>
            {COLOR_PALETTE.map((c) => (
              <Grid item xs={3} key={c.name}>
                <Box
                  onClick={() => handleSelectColor(c.value)}
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 1.5,
                    backgroundColor: c.value,
                    border: '2px solid #CBD5E1',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    '&:hover': { transform: 'scale(1.15)', borderColor: '#2563EB' }
                  }}
                  title={c.name}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>

      {/* MODAL ALTA RÁPIDA DE CLIENTE */}
      <Modal open={modalClienteOpen} onClose={() => setModalClienteOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            👤 Nuevo Cliente Detectado
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            El cliente introducido no existe. Por favor, completa los datos obligatorios para guardarlo:
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Nombre *"
              value={nuevoCliente.nombre}
              onChange={(e) => setNuevoCliente({ ...nuevoCliente, nombre: e.target.value })}
              fullWidth
            />
            <TextField
              label="Apellidos *"
              value={nuevoCliente.apellidos}
              onChange={(e) => setNuevoCliente({ ...nuevoCliente, apellidos: e.target.value })}
              fullWidth
            />
            <TextField
              label="Teléfono *"
              value={nuevoCliente.telefono}
              onChange={(e) => setNuevoCliente({ ...nuevoCliente, telefono: e.target.value })}
              fullWidth
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button onClick={() => setModalClienteOpen(false)}>Cancelar</Button>
              <Button variant="contained" color="primary" onClick={handleSaveNuevoCliente}>
                Guardar Cliente
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      {/* MODAL AÑADIR PERSONAL AL DÍA */}
      <Modal open={modalPersonalOpen} onClose={() => setModalPersonalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            👥 Añadir Personal a {nuevoPersonal.fecha}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Nombre de la Persona *"
              value={nuevoPersonal.nombre}
              onChange={(e) => setNuevoPersonal({ ...nuevoPersonal, nombre: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Función *</InputLabel>
              <Select
                value={nuevoPersonal.tipo_personal}
                label="Función *"
                onChange={(e) => setNuevoPersonal({ ...nuevoPersonal, tipo_personal: e.target.value })}
              >
                <MenuItem value="TERAPEUTA">TERAPEUTA (THER)</MenuItem>
                <MenuItem value="RECEPCIONISTA">RECEPCIONISTA (RECEP)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Estado / Turno (ej: VC, BA, OFF, 10:00-19:00)"
              value={nuevoPersonal.estado_horario}
              onChange={(e) => setNuevoPersonal({ ...nuevoPersonal, estado_horario: e.target.value })}
              fullWidth
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button onClick={() => setModalPersonalOpen(false)}>Cancelar</Button>
              <Button variant="contained" color="primary" onClick={handleSaveNuevoPersonal}>
                Guardar Personal
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      {/* POPOVER CALENDARIO INTERACTIVO */}
      <Popover
        open={Boolean(calendarAnchorEl)}
        anchorEl={calendarAnchorEl}
        onClose={() => setCalendarAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            p: 2,
            width: 310,
            borderRadius: 2,
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
            bgcolor: '#0F172A',
            color: '#F8FAFC'
          }
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <IconButton
            size="small"
            onClick={() => {
              if (pickerMonth === 1) {
                setPickerMonth(12);
                setPickerYear(pickerYear - 1);
              } else {
                setPickerMonth(pickerMonth - 1);
              }
            }}
            sx={{ color: '#94A3B8', '&:hover': { color: '#FFF' } }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#FFF' }}>
            {MESES[pickerMonth - 1]} {pickerYear}
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              if (pickerMonth === 12) {
                setPickerMonth(1);
                setPickerYear(pickerYear + 1);
              } else {
                setPickerMonth(pickerMonth + 1);
              }
            }}
            sx={{ color: '#94A3B8', '&:hover': { color: '#FFF' } }}
          >
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Grid container columns={7} sx={{ mb: 1, textAlign: 'center' }}>
          {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d, i) => (
            <Grid item xs={1} key={i}>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 'bold' }}>
                {d}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Grid container columns={7} spacing={0.5}>
          {getCalendarMatrix(pickerYear, pickerMonth).map((dayNum, idx) => {
            if (dayNum === null) {
              return <Grid item xs={1} key={`empty-${idx}`} />;
            }
            const dateStr = `${pickerYear}-${String(pickerMonth).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
            const todayStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;
            const isToday = dateStr === todayStr;

            return (
              <Grid item xs={1} key={dateStr}>
                <Box
                  onClick={() => {
                    scrollToDate(dateStr);
                    setCalendarAnchorEl(null);
                  }}
                  sx={{
                    width: '100%',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: isToday ? 'bold' : 500,
                    cursor: 'pointer',
                    bgcolor: isToday ? '#2563EB' : 'transparent',
                    color: isToday ? '#FFF' : '#E2E8F0',
                    border: isToday ? '1px solid #60A5FA' : '1px solid transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: isToday ? '#1D4ED8' : '#334155',
                      color: '#FFF',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  {dayNum}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Popover>

      {/* SPINNER OVERLAY A PANTALLA COMPLETA DURANTE LA CARGA DE LA AGENDA */}
      {isLoadingMonth && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(6px)',
            zIndex: 999999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2.5,
            pointerEvents: 'all'
          }}
        >
          <CircularProgress
            size={64}
            thickness={4.5}
            sx={{
              color: '#38BDF8',
              filter: 'drop-shadow(0 0 10px rgba(56, 189, 248, 0.5))'
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: '#F8FAFC',
              fontWeight: 700,
              letterSpacing: 0.5,
              textShadow: '0 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            Cargando agenda...
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#94A3B8',
              letterSpacing: 0.25
            }}
          >
            Por favor espere mientras se cargan los datos del mes
          </Typography>
        </Box>
      )}
    </Box>
  );
}
